"use client";

import {
  createContext, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import {
  encodeMatterportViewFragment, isMatterportViewAvailable, matterportEnabledSweeps,
  matterportSessionKey, matterportViewId, parseMatterportSweepFragment, parseMatterportViewFragment,
  restoreMatterportView, snapshotMatterportView,
  type MatterportSessionSdk, type MatterportViewSnapshot,
} from "@/lib/virtualspace/matterport-viewer-session";

export type LaunchViewerSessionControls = {
  modelId: string;
  shareFragment: string | null;
  canResume: boolean;
  canStartOver: boolean;
  busy: boolean;
  status: string | null;
  /** Read at the share click, so throttled menu renders do not share an older frame. */
  getShareFragment(): string | null;
  resume(): void;
  startOver(): void;
};

type Memory = { position: MatterportViewSnapshot; initial: MatterportViewSnapshot };
type SessionStore = {
  memory: Map<string, Memory>;
  /** Hashes are consumed once per provider lifetime, including failed restores. */
  consumed: Set<string>;
  publish(owner: object, controls: LaunchViewerSessionControls): void;
  clear(owner: object): void;
};

const StoreContext = createContext<SessionStore | null>(null);
const ControlsContext = createContext<LaunchViewerSessionControls | null>(null);

/** Memory is intentionally confined to this Launch page's mounted lifetime. */
export function LaunchViewerSessionProvider({ children }: { children: ReactNode }) {
  const [controls, setControls] = useState<LaunchViewerSessionControls | null>(null);
  const owner = useRef<object | null>(null);
  const store = useMemo<SessionStore>(() => ({
    memory: new Map(), consumed: new Set(),
    publish(nextOwner, next) { owner.current = nextOwner; setControls(next); },
    clear(previousOwner) {
      if (owner.current !== previousOwner) return;
      owner.current = null;
      setControls(null);
    },
  }), []);
  return (
    <StoreContext.Provider value={store}>
      <ControlsContext.Provider value={controls}>{children}</ControlsContext.Provider>
    </StoreContext.Provider>
  );
}

export function useLaunchViewerSessionControls(): LaunchViewerSessionControls | null {
  return useContext(ControlsContext);
}

/**
 * Mount only for the active, ready Launch SDK. No model is loaded to read memory.
 * An incoming shared position wins over remembered state. A normal return offers
 * Resume explicitly, keeping startup emissions from replacing the remembered spot.
 */
export function useLaunchViewerSession({ sdk: sdkValue, modelId, reducedMotion = false }: {
  sdk: unknown;
  modelId: string;
  reducedMotion?: boolean;
}): void {
  const store = useContext(StoreContext);
  const reducedMotionRef = useRef(reducedMotion);
  useEffect(() => { reducedMotionRef.current = reducedMotion; }, [reducedMotion]);

  useEffect(() => {
    if (!store || !sdkValue) return;
    const sdk = sdkValue as MatterportSessionSdk;
    if (!sdk.Camera?.pose?.subscribe) return;
    const owner = {};
    let active = true;
    let subscriptionsReady = false;
    let viewReady = !sdk.View?.current?.subscribe;
    let sweepsReady = !sdk.Sweep?.data?.subscribe;
    let zoomReady = !sdk.Camera?.zoom?.subscribe;
    let rawPose: unknown;
    let rawZoom: unknown;
    let viewId: string | undefined;
    let enabledSweeps: ReadonlySet<string> = new Set();
    let initial: MatterportViewSnapshot | null = null;
    let current: MatterportViewSnapshot | null = null;
    let pendingResume: MatterportViewSnapshot | null = null;
    let initializedKey: string | null = null;
    let operation: AbortController | null = null;
    let busy = false;
    let status: string | null = null;
    let publishTimer: ReturnType<typeof setTimeout> | undefined;
    const subscriptions: Array<{ cancel(): void }> = [];
    const getScope = () => ({ modelId, viewId, enabledSweeps });
    const available = (snapshot: MatterportViewSnapshot | null): snapshot is MatterportViewSnapshot =>
      !!snapshot && isMatterportViewAvailable(snapshot, getScope());
    const equal = (a: MatterportViewSnapshot | null, b: MatterportViewSnapshot | null) =>
      !!a && !!b && encodeMatterportViewFragment(a) === encodeMatterportViewFragment(b);

    function save() {
      if (initializedKey && current && initial && !pendingResume && !busy && available(current)) {
        store!.memory.set(initializedKey, { position: current, initial });
      }
    }

    function publish(immediate = false) {
      if (!active) return;
      const send = () => {
        publishTimer = undefined;
        if (!active) return;
        store!.publish(owner, {
          modelId,
          shareFragment: !busy && available(current) ? encodeMatterportViewFragment(current) : null,
          canResume: !busy && available(pendingResume),
          canStartOver: !busy && available(initial) && (!!pendingResume || !equal(initial, current)),
          busy, status,
          getShareFragment: () => active && !busy && available(current) ? encodeMatterportViewFragment(current) : null,
          resume() { if (available(pendingResume)) void navigate(pendingResume, "resume"); },
          startOver() { if (available(initial)) void navigate(initial, "start"); },
        });
      };
      if (immediate) { clearTimeout(publishTimer); send(); }
      else if (publishTimer === undefined) publishTimer = setTimeout(send, 200);
    }

    async function navigate(target: MatterportViewSnapshot, reason: "shared" | "resume" | "start") {
      if (!active || busy || !available(target)) return;
      const controller = new AbortController();
      operation = controller;
      busy = true;
      status = reason === "shared" ? "Opening shared position…" : reason === "resume" ? "Resuming your position…" : "Returning to the start…";
      publish(true);
      try {
        const restored = await restoreMatterportView({
          sdk, snapshot: target, getScope, signal: controller.signal,
          reducedMotion: reducedMotionRef.current,
        });
        if (!active || operation !== controller || controller.signal.aborted) return;
        if (restored) {
          current = target;
          pendingResume = null;
          status = reason === "shared" ? "Shared position opened." : reason === "resume" ? "Position resumed." : "Returned to the start.";
        } else {
          if (reason !== "resume") pendingResume = null;
          status = "That position is no longer available in this tour.";
        }
      } catch {
        if (!active || operation !== controller || controller.signal.aborted) return;
        status = "Could not move to that position. Try again.";
      } finally {
        if (active && operation === controller) {
          operation = null;
          busy = false;
          save();
          publish(true);
        }
      }
    }

    function sharedPosition() {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.slice(1));
      if ((!params.has("vp") && !params.has("sweep")) || store!.consumed.has(hash)) return null;
      const parsed = params.has("vp") ? parseMatterportViewFragment(hash) : parseMatterportSweepFragment(hash, viewId);
      // The fragment can be waiting for #m reconciliation in the parent. Never
      // consume another model's valid state or use it to select that model.
      if (parsed && parsed.modelId !== modelId) return null;
      store!.consumed.add(hash);
      if (!parsed || !available(parsed)) {
        status = "This shared position is unavailable. Showing the tour's starting view.";
        return null;
      }
      return parsed;
    }

    function update() {
      if (!active || !subscriptionsReady || !viewReady || !sweepsReady || !zoomReady) return;
      const snapshot = snapshotMatterportView(modelId, rawPose, viewId, rawZoom);
      if (!snapshot || !available(snapshot)) { current = null; publish(true); return; }
      const key = matterportSessionKey(modelId, viewId);
      if (initializedKey !== key) {
        initializedKey = key;
        current = snapshot;
        const saved = store!.memory.get(key);
        initial = saved && available(saved.initial) ? saved.initial : snapshot;
        pendingResume = saved && available(saved.position) && !equal(saved.position, snapshot) ? saved.position : null;
        const shared = sharedPosition();
        if (shared) { pendingResume = null; void navigate(shared, "shared"); return; }
        save();
        publish(true);
        return;
      }
      current = snapshot;
      // SDK startup animations may continue after the playing phase. Preserve a
      // Resume candidate until an explicit Resume or Start over action chooses
      // its replacement; observable changes alone cannot distinguish intent.
      save();
      publish();
    }

    const subscribe = (attach: () => { cancel(): void }) => {
      try { subscriptions.push(attach()); } catch {
        status = "Position sharing is unavailable in this tour.";
      }
    };
    if (sdk.View?.current?.subscribe) subscribe(() => sdk.View!.current!.subscribe((value) => {
      if (!active) return;
      const next = matterportViewId(value);
      if (viewReady && next !== viewId) {
        save();
        operation?.abort(); operation = null; busy = false;
        current = initial = pendingResume = null;
        initializedKey = null; rawPose = undefined;
        status = null;
      }
      viewId = next;
      // An exposed but unresolved View must not degrade to an unscoped snapshot.
      viewReady = next !== undefined;
      update(); publish(true);
    }));
    if (sdk.Sweep?.data?.subscribe) subscribe(() => sdk.Sweep!.data!.subscribe({
      onCollectionUpdated(collection) {
        if (!active) return;
        enabledSweeps = matterportEnabledSweeps(collection);
        sweepsReady = true;
        update();
      },
    }));
    if (sdk.Camera?.zoom?.subscribe) subscribe(() => sdk.Camera!.zoom!.subscribe((value) => {
      if (!active) return;
      rawZoom = value; zoomReady = true; update();
    }));
    subscribe(() => sdk.Camera!.pose!.subscribe((value) => {
      if (!active) return;
      rawPose = value; update();
    }));
    subscriptionsReady = true;
    update();

    const onHashChange = () => {
      if (!active || !initializedKey || busy) return;
      const shared = sharedPosition();
      if (shared) { pendingResume = null; void navigate(shared, "shared"); }
      else publish(true);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => {
      save();
      active = false;
      operation?.abort();
      clearTimeout(publishTimer);
      window.removeEventListener("hashchange", onHashChange);
      for (const subscription of subscriptions) {
        try { subscription.cancel(); } catch { /* SDK disconnect can win cleanup. */ }
      }
      store.clear(owner);
    };
  }, [sdkValue, modelId, store]);
}
